import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React from "react";
import { useTranslation } from "react-i18next";
import { TouchableOpacity } from "react-native";
import useCredits from "../../Hooks/useCredits";
import useWorkspaces from "../../Hooks/useWorkspaces";
import {
  RECHARGE_STATES,
  formatUsd,
} from "../../Constants/billing";

// CreditsIndicator: a simple status pill for the top bar. Shows the current
// consumption status — out of credits / UoI budget reached / running low /
// available. The two "paused" states are driven by the SAME backend signals the
// request-time gate enforces (workspace.paymentFailed / workspace.disabled), so
// the pill always matches what is actually blocking consumption.
// No buttons, no popover, no detail. Only shown inside a "business"
// (consumption) workspace. Clicking it sends the user to the billing page.
const CreditsIndicator = ({ onOpenSettings }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const AppToasty = AppToast.useToast();
  const { user } = AppUser.useUser();
  const credits = useCredits();
  const { getActiveWorkspace, getEffectiveSubscriptionType } = useWorkspaces();

  if (!credits?.enabled) return null;

  const activeWorkspace = getActiveWorkspace();
  const effectiveType = getEffectiveSubscriptionType(activeWorkspace);
  if (effectiveType !== "business") return null;

  // Xero-billed workspaces are invoiced separately (not prepaid credits), so
  // the credit indicator / paused state does not apply there.
  if (activeWorkspace?.billingProvider === "XERO") return null;

  // Authoritative pause signals (mirror the backend enforcement).
  const isPaymentFailed = activeWorkspace?.paymentFailed === true;
  const isDisabled = activeWorkspace?.disabled === true;

  // Balance-based states (used only when no backend pause flag is set).
  const isExhausted = credits.rechargeState === RECHARGE_STATES.EXHAUSTED;
  const isLow = credits.rechargeState === RECHARGE_STATES.LOW;

  // Precedence: backend pause flags first, then balance-based state.
  const outOfCredits = isPaymentFailed || isExhausted;
  const budgetReached = !isPaymentFailed && isDisabled;
  const isWarning = budgetReached || isLow;

  const color = outOfCredits
    ? currentTheme?.theme?.red
    : isWarning
    ? currentTheme?.theme?.orange
    : currentTheme?.theme?.green;

  const message = outOfCredits
    ? t("credits.indicator.exhausted")
    : budgetReached
    ? t("credits.indicator.limitReached", {
        defaultValue: "Root monthly spend limit reached",
      })
    : isLow
    ? t("credits.indicator.low")
    : t("credits.indicator.available", {
        amount: formatUsd(credits.balanceUsd),
      });

  // Only the workspace owner can resolve a paused/spend-limit state. Members
  // clicking the pill are told to contact the root admin instead of being
  // redirected to their own billing section (which couldn't fix anything).
  const isOwner = activeWorkspace?.userID === user?.id;
  const handlePress = () => {
    if (isOwner) {
      onOpenSettings();
    } else {
      AppToasty.show(t("workspaces.rootAdminLimitReached"), {
        type: "normal",
      });
    }
  };

  return (
    <TouchableOpacity
      onPress={handlePress}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        marginRight: 8,
        marginTop: 4,
        paddingVertical: 2,
        paddingHorizontal: 8,
        borderRadius: 12,
        backgroundColor: outOfCredits
          ? "rgba(255,0,0,0.08)"
          : isWarning
          ? "rgba(255,165,0,0.08)"
          : currentTheme?.theme?.softBackgroundColor,
      }}
    >
      <AppIcon
        icon={AppAssets.icons.cost}
        color={color}
        size={14}
        wrapperStyle={{ marginRight: 2 }}
      />
      <AppText
        style={{ fontSize: currentTheme?.theme?.extra_small_font_size }}
        color={color}
        fontWeight="bold"
      >
        {message}
      </AppText>
    </TouchableOpacity>
  );
};

export default CreditsIndicator;
